import { useRef, type ReactNode } from 'react';
import { Button, Dialog, DialogContent } from '@ledgerloom/ui';

// A modal confirmation (the library's dialog): focus is trapped, Escape cancels,
// and focus returns to whatever opened it. It opens on the body's first field, or
// on Cancel when there is none. The confirm label names the outcome. Pattern: overlays/modal.
export function ConfirmDialog({ open, title, children, confirmLabel, tone = 'primary', busy, onConfirm, onCancel, returnFocus }: {
  open: boolean; title: string; children: ReactNode; confirmLabel: string; tone?: 'primary' | 'danger';
  busy?: boolean; onConfirm: () => void; onCancel: () => void;
  // Where focus goes on close, when the opener was disabled or replaced while the dialog opened.
  returnFocus?: HTMLElement | null;
}) {
  const body = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onCancel(); }}>
      <DialogContent
        title={title}
        hideClose
        className="w-[min(35rem,calc(100vw-2rem))]"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          opener.current = returnFocus ?? (document.activeElement as HTMLElement | null);
          const field = body.current?.querySelector<HTMLElement>('input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), select:not(:disabled)');
          (field ?? cancel.current)?.focus();
        }}
        onCloseAutoFocus={(e) => { e.preventDefault(); opener.current?.focus(); }}
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div ref={body} className="grid gap-2.5 text-sm text-foreground [&_ol:not([class])]:list-decimal [&_ol:not([class])]:pl-5 [&_ul:not([class])]:list-disc [&_ul:not([class])]:pl-5">{children}</div>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button ref={cancel} variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant={tone} onClick={onConfirm} disabled={busy}>{busy ? 'Sending…' : confirmLabel}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
